import { TFunction } from 'react-i18next'
import {
  capitalize,
  concat,
  find,
  get,
  last,
  isEmpty,
  map,
  filter,
  split,
  includes,
  sortBy,
  join,
  has,
} from 'lodash'
import { ColumnsType } from 'antd/lib/table'
import { IOrderResponse } from '../../../../interface/order-response.interface'
import { Avatar, Image, Progress, Row, Space } from 'antd'
import { Link } from 'react-router-dom'
import OrderTablePopUpMenu from './components/OrderTablePopUpMenu/OrderTablePopUpMenu'
import moment from 'moment'
import { UserOutlined } from '@ant-design/icons'
import { handleTableDefaultSortColumns, isEmptyValue } from '@/utils/common'
import {
  AllowEditOrder,
  IsCreator,
  IsExternalUser,
} from '@/screens/Order/Pages/EditOrder/EditOrder.helper'
import OrderAvatarGroup from './components/OrderAvatarGroup/OrderAvatarGroup'
import { deleteOrderRef } from './OrderTable.service'
import { IMG_PLACE_HOLDER } from '@/constant/common'
import { IsAdminRole, IsSupplierRole } from '@/utils/users'
import {
  DISPLAY_DEFAULT_COLUMN,
  DISPLAY_DEFAULT_COLUMN_TABLE,
  DROP_COLUMNS_END,
  DROP_COLUMNS_START,
} from '../../../Order.constant'
import {
  ORDER_PROGRESS_STATUS_HAS,
  PROGRESS_STATUS_DELIVERED,
  PROGRESS_STATUS_ONTRACK,
  PROGRESS_STATUS_UNKNOWN,
  PROGRESS_STATUS_URGENT,
  PROGRESS_STATUS_WARNING,
} from '@/constant/pbf'

const getPropertyValue = (record, field) => {
  const data =
    get(
      find(get(record, 'property_values'), {
        field: field,
      }),
      'value'
    ) ??
    get(
      find(get(record, 'property_value.values'), {
        field: field,
      }),
      'value'
    )

  return !isEmptyValue(data) ? data : '-'
}

export const getValueDynamic = (
  obj: any,
  dataSource: string,
  getValue = true
) => {
  const [main, fieldName] = split(dataSource, ':')
  const mainValue = get(obj, main)

  if (!fieldName || !mainValue) {
    return mainValue ?? '-'
  }

  const found = find(mainValue, ({ field }) => field === fieldName)

  if (!getValue) return found
  const foundDataType = get(found, 'data_type')
  const foundValue = get(found, 'value') ?? '-'

  try {
    let result = foundValue
    if (includes(['date', 'datetime'], foundDataType)) {
      result = moment(foundValue).format('DD MMM YYYY')
    } else if (includes(['multi_select'], foundDataType)) {
      const passJson = JSON.parse(foundValue)
      result = join(passJson, ', ') ?? '-'
    }

    return result
  } catch {
    return foundValue
  }
}
const onColorChange = (progressStatus) => {
  let resultColor = '#848484' // default

  if (includes([PROGRESS_STATUS_ONTRACK], progressStatus)) {
    resultColor = '#4A8DC0'
  }

  if (includes([PROGRESS_STATUS_WARNING], progressStatus)) {
    resultColor = '#E67E22'
  }

  if (includes([PROGRESS_STATUS_URGENT], progressStatus)) {
    resultColor = '#C03261'
  }

  if (includes([PROGRESS_STATUS_DELIVERED], progressStatus)) {
    resultColor = '#65BF73'
  }

  return resultColor
}

function IsExternal(orderCompanyId, userCompanyId) {
  return orderCompanyId != userCompanyId
}

export enum SORT_ORDER {
  order_number = 'order.number',
  to_be_delivered_date = 'order.to_be_delivered_date',
  supplier_name = 'supplier.name',
  factory_name = 'factory.name',
  country = 'pv.country',
  progress = 'order.progress',
  progress_status = 'order.progress_status',
}

const handleRenderColumnsOrderNumber = (defaultLayout, user, sorter) => {
  const isEmptyGroupBy = isEmpty(localStorage.getItem('groupBy'))

  const renderRecord = (record) => {
    const isChildren = has(record, 'children')

    if (isChildren) {
      return (
        <div
          style={{
            fontWeight: 600,
            display: 'inline-block',
            maxWidth: '120px',
          }}
        >
          {`${
            record.key === 'orderStatus'
              ? capitalize(record?.value)
              : record?.value
          }
              `}
          <br />
          <small>{`${record?.totalOrder} Orders`}</small>
        </div>
      )
    }

    const getCompanyID = get(record, 'company.id')
    const getCompanyName = get(record, 'company.name')
    const getPicture: any = get(record, 'picture')
    const getRecordNumber = get(record, 'number')
    const getRecordId = get(record, 'id')

    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Image
          loading="lazy"
          alt="logo"
          preview={false}
          src={getPicture?.url_path ?? 'error'}
          fallback={IMG_PLACE_HOLDER}
          style={{
            borderRadius: '10px',
            backgroundColor: 'white',
            width: '48px',
            height: '48px',
            objectFit: 'cover',
          }}
        />
        <div>
          <Row align={'middle'}>
            <Link
              to={
                getRecordNumber && getRecordId
                  ? `/pbf/orders/${encodeURIComponent(
                      getRecordNumber
                    )}?orderKey=${getRecordId}&activeTab=1`
                  : '#'
              }
            >
              <span
                className={`${
                  getRecordNumber && getRecordId
                    ? 'landing-page-orderNo'
                    : 'color-dark'
                }`}
                style={{
                  display: 'block',
                  marginLeft: 8,
                  fontWeight: 600,
                  color: '#4a8dc0',
                }}
              >
                {getRecordNumber}
              </span>
            </Link>
            {IsExternal(getCompanyID, user.company.id) && (
              <p className="external-text">Ext</p>
            )}
          </Row>
          {IsExternal(getCompanyID, user.company.id) && (
            <p className="external-company-name">{getCompanyName}</p>
          )}
        </div>
      </div>
    )
  }
  // Improve performance sorting after
  const tempSorter = isEmptyGroupBy
    ? {
        sortOrder: handleTableDefaultSortColumns(
          SORT_ORDER,
          sorter,
          SORT_ORDER.order_number
        ),
        sorter: true,
      }
    : {
        sorter: (a, b) => {
          if (isEmpty(a.number)) return a?.value?.localeCompare(b?.value)
          return a?.number?.localeCompare(b?.number)
        },
      }

  return {
    ...defaultLayout,
    ...tempSorter,
    render: (_, record) => renderRecord(record),
  }
}

const handleRenderColumnsAction = (defaultLayout, user) => {
  const renderRecord = (record) => {
    const isChildren = has(record, 'children')

    if (isChildren) {
      return '-'
    }
    const getAssignment = get(record, 'assignments')

    return (
      <OrderTablePopUpMenu
        editable={AllowEditOrder(getAssignment, user)}
        clone={!IsSupplierRole(user) && !IsExternalUser(record, user)}
        deletable={
          (!IsExternalUser(record, user) && IsAdminRole(user)) ||
          (!IsExternalUser(record, user) && IsCreator(record, user))
        }
        hideActions={defaultLayout.hideActions}
        data={{
          id: (record as any)?.id ?? '',
          number: (record as any)?.number ?? '',
          image: (record as any)?.picture?.url_path ?? '',
          name: getPropertyValue(record, 'masterOrder'),
        }}
      />
    )
  }
  return {
    ...defaultLayout,
    render: (_, record) => renderRecord(record),
  }
}

const handleRenderDeliverDate = (defaultLayout, user, sorter) => {
  const isEmptyGroupBy = isEmpty(localStorage.getItem('groupBy'))

  const renderRecord = (record) => {
    const isChildren = has(record, 'children')

    if (isChildren) {
      return '-'
    }

    const getDate = get(record, 'to_be_delivered_date')

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {moment(getDate).format('DD MMM YYYY')}
      </div>
    )
  }

  // Improve performance sorting after
  const tempSorter = isEmptyGroupBy
    ? {
        sorter: true,
        sortOrder: handleTableDefaultSortColumns(
          SORT_ORDER,
          sorter,
          SORT_ORDER.to_be_delivered_date
        ),
      }
    : {
        sorter: (a, b) => {
          return (
            moment(a?.to_be_delivered_date).unix() -
            moment(b?.to_be_delivered_date).unix()
          )
        },
      }

  return {
    ...defaultLayout,
    ...tempSorter,
    render: (_, record) => renderRecord(record),
  }
}

const handleRenderSupplier = (defaultLayout, user, sorter) => {
  const isEmptyGroupBy = isEmpty(localStorage.getItem('groupBy'))

  const renderRecord = (record) => record?.supplier?.name ?? '-'

  const tempSorter = isEmptyGroupBy
    ? {
        sorter: true,
        sortOrder: handleTableDefaultSortColumns(
          SORT_ORDER,
          sorter,
          SORT_ORDER.supplier_name
        ),
      }
    : {
        sorter: (a, b) => {
          const aSupplier = a?.supplier?.name ?? ''
          const bSupplier = b?.supplier?.name ?? ''
          return aSupplier.localeCompare(bSupplier)
        },
      }

  return {
    ...defaultLayout,
    ...tempSorter,
    render: (_, record) => renderRecord(record),
  }
}

const handleRenderFactory = (defaultLayout, user, sorter) => {
  const isEmptyGroupBy = isEmpty(localStorage.getItem('groupBy'))

  const renderRecord = (record) => record?.factory?.name ?? '-'

  const tempSorter = isEmptyGroupBy
    ? {
        sorter: true,
        sortOrder: handleTableDefaultSortColumns(
          SORT_ORDER,
          sorter,
          SORT_ORDER.factory_name
        ),
      }
    : {
        sorter: (a, b) => {
          const aFactory = a?.factory?.name ?? ''
          const bFactory = b?.factory?.name ?? ''
          return aFactory.localeCompare(bFactory)
        },
      }

  return {
    ...defaultLayout,
    ...tempSorter,
    render: (_, record) => renderRecord(record),
  }
}

const handleRenderCountry = (defaultLayout, user, sorter) => {
  const isEmptyGroupBy = isEmpty(localStorage.getItem('groupBy'))

  const renderRecord = (record) =>
    getValueDynamic(record, 'property_values:country')

  const tempSorter = isEmptyGroupBy
    ? {
        sorter: true,
        sortOrder: handleTableDefaultSortColumns(
          SORT_ORDER,
          sorter,
          SORT_ORDER.country
        ),
      }
    : {
        sorter: (a, b) => {
          const aCountry =
            getValueDynamic(a, 'property_values:country', true) ?? ''
          const bCountry =
            getValueDynamic(b, 'property_values:country', true) ?? ''
          return aCountry.localeCompare(bCountry)
        },
      }

  return {
    ...defaultLayout,
    ...tempSorter,
    render: (_, record) => renderRecord(record),
  }
}

const handleRenderProgress = (defaultLayout, user, sorter) => {
  const isEmptyGroupBy = isEmpty(localStorage.getItem('groupBy'))

  const renderRecord = (record) => {
    const isChildren = has(record, 'children')

    if (isChildren) {
      return '-'
    }

    const getProgress = get(record, 'progress') || 0
    const getProgressStatus = get(record, 'progress_status')
    const progressStatus = ORDER_PROGRESS_STATUS_HAS.includes(getProgressStatus)
      ? getProgressStatus
      : PROGRESS_STATUS_UNKNOWN

    return (
      <div className="flex-center">
        <Progress
          strokeColor={onColorChange(progressStatus)}
          percent={getProgress}
          showInfo={false}
          style={{ width: '80%' }}
        />
        <span className="percentage">{getProgress}%</span>
      </div>
    )
  }

  const tempSorter = isEmptyGroupBy
    ? {
        sorter: true,
        sortOrder: handleTableDefaultSortColumns(
          SORT_ORDER,
          sorter,
          SORT_ORDER.progress
        ),
      }
    : {
        sorter: (a, b) => {
          const aProgress = get(a, 'progress') || 0
          const bProgress = get(b, 'progress') || 0
          return aProgress - bProgress
        },
      }

  return {
    ...defaultLayout,
    ...tempSorter,
    render: (_, record) => renderRecord(record),
  }
}

const handleRenderProgressStatus = (defaultLayout, user, sorter) => {
  const isEmptyGroupBy = isEmpty(localStorage.getItem('groupBy'))

  const renderRecord = (record) => {
    const isChildren = has(record, 'children')

    if (isChildren) {
      return '-'
    }

    let getProgressStatus: string = get(record, 'progress_status')
    const getIsDelivered = get(record, 'is_delivered')

    // Special case for order has been delivered but progress status not updated
    if (getIsDelivered) {
      getProgressStatus = PROGRESS_STATUS_DELIVERED
    }

    const statusKey = String(getProgressStatus)
    const displayStatus =
      statusKey === PROGRESS_STATUS_UNKNOWN
        ? PROGRESS_STATUS_UNKNOWN
        : statusKey

    let tagClass = 'tag'
    switch (statusKey) {
      case PROGRESS_STATUS_URGENT:
        tagClass = 'tag red'
        break
      case PROGRESS_STATUS_WARNING:
        tagClass = 'tag yellow'
        break
      case PROGRESS_STATUS_ONTRACK:
        tagClass = 'tag'
        break
      case PROGRESS_STATUS_DELIVERED:
        tagClass = 'tag green'
        break
      case PROGRESS_STATUS_UNKNOWN:
      default:
        tagClass = 'tag gray'
        break
    }
    const displayText =
      displayStatus === PROGRESS_STATUS_UNKNOWN
        ? 'New'
        : capitalize(displayStatus)

    return <span className={tagClass}>{displayText}</span>
  }

  const tempSorter = isEmptyGroupBy
    ? {
        sorter: true,
        sortOrder: handleTableDefaultSortColumns(
          SORT_ORDER,
          sorter,
          SORT_ORDER.progress_status
        ),
      }
    : {
        sorter: (a, b) => {
          const aStatus = get(a, 'progress_status') ?? ''
          const bStatus = get(b, 'progress_status') ?? ''
          return aStatus.localeCompare(bStatus)
        },
      }

  return {
    ...defaultLayout,
    ...tempSorter,
    render: (_, record) => renderRecord(record),
  }
}

const handleRenderCreator = (defaultLayout) => {
  const renderRecord = (record) => {
    const isChildren = has(record, 'children')

    if (isChildren) {
      return '-'
    }
    const getCreator: any = get(record, 'created_by', {})
    const firstName = getCreator?.first_name ?? ''
    const lastName = getCreator?.last_name ?? ''
    const name = `${firstName} ${lastName ? lastName.split('')[0] + '.' : ''}`
    return (
      <Space size="middle">
        <Avatar
          icon={<UserOutlined />}
          src={get(record, 'created_by.avatar.url_path')}
        />
        <span>{name}</span>
      </Space>
    )
  }
  return {
    ...defaultLayout,
    render: (_, record) => renderRecord(record),
  }
}

const handleRenderCollaborators = (defaultLayout) => {
  const renderRecord = (record) => {
    const isChildren = has(record, 'children')
    if (isChildren) {
      return '-'
    }
    const getAssignment = get(record, 'assignments')

    return <OrderAvatarGroup data={getAssignment} />
  }
  return {
    ...defaultLayout,
    render: (_, record) => renderRecord(record),
  }
}

const checkDefaultSort = (sorter, key) => {
  if (!isEmptyValue(sorter)) {
    const sort = sorter.split('.')
    if (key === last(sort)) {
      return sorter.includes('-') ? 'descend' : 'ascend'
    }
  }
  return null
}

export const nestedOrderColumns = (
  display: string[],
  t: TFunction<'translation', undefined>,
  user?: any,
  dataOrderColumns?: any,
  hideActions?: any,
  sorter?: any
) => {
  const reRenderColumnFN = (item: any) => {
    if (isEmpty(item)) return {}

    const dynamicF = {
      order_number: handleRenderColumnsOrderNumber,
      action: handleRenderColumnsAction,
      deliveryDate: handleRenderDeliverDate,
      supplier: handleRenderSupplier,
      factory: handleRenderFactory,
      country: handleRenderCountry,
      progress: handleRenderProgress,
      progress_status: handleRenderProgressStatus,
      creator: handleRenderCreator,
      collaborators: handleRenderCollaborators,
    }

    const renderNotCustomColumn = get(dynamicF, item.key)

    // Object record depend on DROP_COLUMNS_END || getOrderColumnTransform

    const resultLayout = {
      title: t(item.value),
      dataIndex: item.key,
      displayOrder: item.displayOrder,
      key: item.key,
      width: item.width,
      fixed: item.fixed,
      defaultFilteredValue: null,
      hideActions, // TODO: tmp: separate hideProperties from layout
      render: (_, record) => getPropertyValue(record, item.key),
    }

    if (renderNotCustomColumn) {
      return {
        ...renderNotCustomColumn(resultLayout, user, sorter),
      }
    } else {
      const columnKey = get(item, 'key')
      const valueSortKey = get(item, 'sortable.key', '')
      const isSortStatus = get(item, 'sortable.status', false)
      const isSortCustomColumn = valueSortKey && isSortStatus
      const customSorterAllow = { [columnKey]: valueSortKey }
      const tempSorter = {
        sorter: isSortCustomColumn,
        sortOrder: handleTableDefaultSortColumns(
          customSorterAllow,
          sorter,
          valueSortKey
        ),
      }

      return {
        ...resultLayout,
        ...tempSorter,
        render: (_, record) => getValueDynamic(record, item.dataSource),
      }
    }
  }

  const getDefaultColumns: ColumnsType<any> = map(
    DISPLAY_DEFAULT_COLUMN_TABLE,
    (item) => reRenderColumnFN(item)
  )

  const getRenderColumns: ColumnsType<any> = map(display, (value) => {
    const found = find(dataOrderColumns, { key: value })
    if (!found) return null
    return reRenderColumnFN(found)
  }).filter(Boolean)
  const filteredField2 = [...getRenderColumns, ...getDefaultColumns]

  return handleDisplayColumnByType(filteredField2)
}

const handleDisplayColumnByType = (finalColumnSelected) => {
  const getKeyStart = map(DROP_COLUMNS_START, 'key')
  const getKeyEnd = map(DROP_COLUMNS_END, 'key')

  const getStartDisplay = sortBy(
    filter(finalColumnSelected, (item) => includes(getKeyStart, item.key)),
    'displayOrder'
  )

  const getEndDisplay = sortBy(
    filter(finalColumnSelected, (item) => includes(getKeyEnd, item.key)),
    'displayOrder'
  )

  const getCustomColumn = sortBy(
    filter(
      finalColumnSelected,
      (item) =>
        !includes(
          concat(getKeyStart, getKeyEnd, DISPLAY_DEFAULT_COLUMN),
          item.key
        )
    ),
    'displayOrder'
  )

  const getDefaultLastDisplay = filter(finalColumnSelected, (item) =>
    includes(DISPLAY_DEFAULT_COLUMN, item.key)
  )

  const finalConcat = concat(
    getStartDisplay,
    getCustomColumn,
    getEndDisplay,
    getDefaultLastDisplay
  )

  return finalConcat
}

export const orderPagination = (
  data: IOrderResponse,
  onPageNumberChange: (page_number) => any
) => {
  return {
    defaultCurrent: data?.page_number ?? 1,
    total: data?.total_record ?? 1,
    showSizeChanger: false,
    pageSize: data?.page_size,
    onChange: (page) => onPageNumberChange(page),
  }
}

export async function deleteOrder(id: string) {
  return await deleteOrderRef(id)
}

/**
 * Transform and normalize order API responses
 * Handles both regular GET and advance filter POST response structures
 */
export function transformOrderData(response: any) {
  try {
    if (!response) {
      return { data: [], pagination: {} }
    }

    if (response?.data?.data && Array.isArray(response.data.data)) {
      return response.data
    }

    return response
  } catch (error) {
    console.error('Error transforming order data:', error)
    return { data: [], pagination: {} }
  }
}
